import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {number} */
  #lastActionTime = 0;

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#ensureCartLine();
    this.updateButtonStates();
    this.#bindDirectClickListeners();

    // Real-time input synchronization with line item thumbnail badge
    this.refs.quantityInput?.addEventListener('input', () => this.syncThumbnailBadge());
    this.syncThumbnailBadge();
  }

  /**
   * Handles component updates after DOM morphing
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.#ensureCartLine();
    this.updateButtonStates();
    this.#bindDirectClickListeners();
    this.syncThumbnailBadge();
  }

  /**
   * Overrides base increaseQuantity with double-click guard
   * @param {Event} [event]
   */
  increaseQuantity(event) {
    event?.preventDefault?.();
    const now = Date.now();
    if (now - this.#lastActionTime < 200) return;
    this.#lastActionTime = now;
    this.updateQuantity(1);
  }

  /**
   * Overrides base decreaseQuantity with double-click guard
   * @param {Event} [event]
   */
  decreaseQuantity(event) {
    event?.preventDefault?.();
    const now = Date.now();
    if (now - this.#lastActionTime < 200) return;
    this.#lastActionTime = now;
    this.updateQuantity(-1);
  }

  /**
   * Updates quantity strictly one by one (+1 or -1) in the cart
   * @param {number} stepMultiplier - Positive for increase (+1), negative for decrease (-1)
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;
    const currentValue = parseInt(quantityInput.value, 10) || 1;
    const effectiveMax = this.getEffectiveMax();

    // Strictly change by 1 (ignoring any multi-pack step attribute)
    const delta = stepMultiplier > 0 ? 1 : -1;
    const minQty = 1;
    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(minQty, currentValue + delta)
    );

    if (newValue === currentValue) return;

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Ensures data-cart-line attribute is present so CartItemsComponent never drops update events
   */
  #ensureCartLine() {
    const { quantityInput } = this.refs;
    if (quantityInput && (!quantityInput.dataset.cartLine || quantityInput.dataset.cartLine === '0')) {
      const row = this.closest('.cart-items__table-row') || this.closest('tr');
      if (row) {
        const allRows = Array.from(document.querySelectorAll('.cart-items__table-row'));
        const idx = allRows.indexOf(row);
        if (idx !== -1) {
          quantityInput.dataset.cartLine = String(idx + 1);
        }
      }
    }
  }

  /**
   * Binds direct click listeners with debounce to guarantee immediate responsiveness without double triggers
   */
  #bindDirectClickListeners() {
    const { minusButton, plusButton } = this.refs;
    if (minusButton && !minusButton._hasDirectClick) {
      minusButton._hasDirectClick = true;
      minusButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.decreaseQuantity(e);
      });
    }

    if (plusButton && !plusButton._hasDirectClick) {
      plusButton._hasDirectClick = true;
      plusButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.increaseQuantity(e);
      });
    }
  }

  /**
   * Updates button states based on current value and limits
   * In the cart, minus is disabled when value <= 1
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    if (!quantityInput) return;
    const value = parseInt(quantityInput.value, 10) || 1;
    const effectiveMax = this.getEffectiveMax();

    if (minusButton) minusButton.disabled = value <= 1;
    if (plusButton) plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles quantity change and synchronizes the thumbnail badge in real-time
   */
  onQuantityChange() {
    this.#ensureCartLine();
    super.onQuantityChange();
    this.syncThumbnailBadge();
  }

  /**
   * Updates cart quantity from data attribute and syncs badge
   */
  updateCartQuantity() {
    super.updateCartQuantity?.();
    this.syncThumbnailBadge();
  }

  /**
   * Synchronizes the numerical badge over the line item image and drawer badge in real-time
   */
  syncThumbnailBadge() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;
    const newQty = parseInt(quantityInput.value, 10) || 0;
    const row = this.closest('.cart-items__table-row') || this.closest('tr');
    if (row) {
      const badge = row.querySelector('.cart-items__quantity-badge');
      if (badge) {
        badge.textContent = String(newQty);
        badge.setAttribute('data-quantity', String(newQty));
        badge.setAttribute('aria-label', `Quantity: ${newQty}`);
        if (newQty > 1) {
          badge.classList.remove('hidden');
          badge.style.display = 'flex';
        } else {
          badge.classList.add('hidden');
          badge.style.display = 'none';
        }
      }
    }

    // Also update drawer header badge if present
    const drawer = this.closest('#cart-drawer') || document.querySelector('#cart-drawer');
    if (drawer) {
      const headerBadge = drawer.querySelector('.theme-drawer__badge');
      if (headerBadge) {
        const allInputs = drawer.querySelectorAll('cart-quantity-selector-component input[type="number"]');
        let totalCount = 0;
        allInputs.forEach((input) => {
          totalCount += parseInt(input.value, 10) || 0;
        });
        if (totalCount > 0) {
          headerBadge.textContent = String(totalCount);
          headerBadge.classList.remove('visually-hidden');
        } else {
          headerBadge.classList.add('visually-hidden');
        }
      }
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
